<template>
  <div>
    <b-modal
      ref="contactModal"
      size="lg"
      hide-header
      hide-footer
      header-class="contact-modal-header"
      @hidden="clearData"
      body-class="p-0"
      class="contact-modal-wrapper"
    >
      <div class="contact-container d-flex">
        <!-- Mobile-only header - only visible on mobile -->
        <div
          class="contact-header w-100 px-4 pt-2 pb-2 d-flex d-md-none justify-content-between align-items-center"
        >
          <h5 class="mb-0 contact-name">
            {{ `${record.first_name || ""} ${record.last_name || ""}` }}
          </h5>
          <div class="contact-actions d-flex">
            <span
              v-if="'id' in record"
              class="modal-btn action-btn"
              v-on:click="editContact"
            >
              <i
                class="update-contact fi-save nav-icon"
                v-b-tooltip.hover.top="'Save Profile'"
              ></i>
            </span>
            <span v-else class="modal-btn action-btn" v-on:click="createGuest">
              <i
                class="create-contact fi-save nav-icon"
                v-b-tooltip.hover.top="'Save Profile'"
              ></i>
            </span>
            <span class="modal-btn action-btn" v-on:click="hide">
              <i
                class="cancel-contact fi-x nav-icon"
                v-b-tooltip.hover.top="'Close Window'"
              ></i>
            </span>
          </div>
        </div>

        <!-- Side Navigation - only visible on desktop -->
        <div class="contact-sidenav d-none d-md-flex">
          <div class="sidenav-header mb-4">
            <div class="nav-section-title text-center">
              <div class="contact-avatar mx-auto mb-2">
                <img
                  v-if="record.photo_uri"
                  :src="record.photo_uri"
                  class="avatar-img"
                  alt="Contact photo"
                />
                <div
                  v-else
                  class="avatar-placeholder d-flex align-items-center justify-content-center"
                >
                  {{ getInitials }}
                </div>
              </div>
              <i class="fi-person-circle sidenav-icon"></i>
            </div>
          </div>

          <div class="contact-nav-items">
            <div
              v-for="(section, index) in sections"
              :key="index"
              class="contact-nav-item"
              :class="{ active: activeSection === section.id }"
              @click="scrollToSection(section.id)"
            >
              <i :class="section.icon"></i>
              <span class="ml-2">{{ section.title }}</span>
            </div>
          </div>

          <!-- Hidden file input for future photo upload functionality -->
          <input
            style="display: none"
            ref="fileInput"
            type="file"
            @change="onFileChanged"
          />
        </div>

        <!-- Main Content Area -->
        <div
          class="contact-content"
          ref="contactContent"
          @scroll="handleScroll"
        >
          <!-- Desktop header - only visible on desktop -->
          <div
            class="desktop-header px-4 pt-2 pb-2 d-none d-md-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0 contact-name">
              {{ `${record.first_name || ""} ${record.last_name || ""}` }}
            </h5>
            <div class="contact-actions d-flex">
              <span
                v-if="'id' in record"
                class="modal-btn action-btn"
                v-on:click="editContact"
              >
                <i
                  class="update-contact fi-save nav-icon"
                  v-b-tooltip.hover.top="'Save Profile'"
                ></i>
              </span>
              <span
                v-else
                class="modal-btn action-btn"
                v-on:click="createGuest"
              >
                <i
                  class="create-contact fi-save nav-icon"
                  v-b-tooltip.hover.top="'Save Profile'"
                ></i>
              </span>
              <span class="modal-btn action-btn" v-on:click="hide">
                <i
                  class="cancel-contact fi-x nav-icon"
                  v-b-tooltip.hover.top="'Close Window'"
                ></i>
              </span>
            </div>
          </div>

          <!-- Main content area with scrollable sections -->
          <div class="contact-body px-4">
            <!-- Personal Information Section -->
            <b-card
              no-body
              class="mb-3 section-card"
              id="profile"
              ref="profile"
            >
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-torso mr-2"></i>
                {{ $t("contact_modal.profile_title") }}
              </b-card-header>
              <b-card-body>
                <b-form>
                  <b-row>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.first')"
                        label-for="NameInput"
                      >
                        <b-form-input
                          v-model="record.first_name"
                          id="NameInput"
                          type="text"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.middle')"
                        label-for="MiddleNameInput"
                      >
                        <b-form-input
                          v-model="record.middle_name"
                          id="MiddleNameInput"
                          type="text"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.last')"
                        label-for="LastNameInput"
                      >
                        <b-form-input
                          v-model="record.last_name"
                          id="LastNameInput"
                          type="text"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.birthday')"
                        label-for="BirthdayInput"
                      >
                        <b-form-input
                          v-model="record.birthday"
                          id="BirthdayInput"
                          type="date"
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.country')"
                        label-for="CountryInput"
                      >
                        <b-form-input
                          v-model="record.country_of_origin"
                          id="CountryInput"
                          type="text"
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.citizenship')"
                        label-for="CitizenshipInput"
                      >
                        <b-form-input
                          v-model="record.citizenship"
                          id="CitizenshipInput"
                          type="text"
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-form>
              </b-card-body>
            </b-card>

            <!-- Contact Information Section -->
            <b-card
              no-body
              class="mb-3 section-card"
              id="contact"
              ref="contact"
            >
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-telephone mr-2"></i>
                {{ $t("contact_modal.contact_title") }}
              </b-card-header>
              <b-card-body>
                <b-form>
                  <b-form-group
                    :label="$t('contact_modal.address')"
                    label-for="AdressInput"
                  >
                    <b-form-input
                      v-model="record.address"
                      id="AdressInput"
                      type="text"
                      required
                    >
                    </b-form-input>
                  </b-form-group>

                  <b-row>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.phone')"
                        label-for="PhoneInput"
                      >
                        <b-form-input
                          v-model="record.phone"
                          id="PhoneInput"
                          type="text"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.email')"
                        label-for="EmailInput"
                      >
                        <b-form-input
                          v-model="record.email"
                          id="EmailInput"
                          type="email"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.facebook')"
                        label-for="FacebookInput"
                      >
                        <b-form-input
                          v-model="record.facebook"
                          id="FacebookInput"
                          type="text"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.instagram')"
                        label-for="InstagramInput"
                      >
                        <b-form-input
                          v-model="record.instagram"
                          id="InstagramInput"
                          type="text"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.twitter')"
                        label-for="TwitterInput"
                      >
                        <b-form-input
                          v-model="record.twitter"
                          id="TwitterInput"
                          type="text"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                    <b-col sm="12" md="12">
                      <b-form-group
                        :label="$t('contact_modal.snapchat')"
                        label-for="SnapchatInput"
                      >
                        <b-form-input
                          v-model="record.snapchat"
                          id="SnapchatInput"
                          type="text"
                          required
                        >
                        </b-form-input>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-form>
              </b-card-body>
            </b-card>

            <!-- Booking History Section -->
            <b-card
              no-body
              class="mb-3 section-card"
              id="bookings"
              ref="bookings"
            >
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-calendar mr-2"></i>
                {{ $t("contact_modal.trips_title") }}
              </b-card-header>
              <b-card-body>
                <div v-if="isRequesting">
                  <b-spinner label="Loading..."></b-spinner>
                </div>

                <div v-else-if="guestTabs.length < 1" class="text-center py-3">
                  <p class="text-muted mb-0">
                    <i class="fi-info-circle mr-2"></i> No Guest History
                  </p>
                </div>

                <div v-else class="booking-history">
                  <b-card
                    class="selected-result-card booking-card mb-2"
                    bg-variant="default"
                    v-for="(guestTab, index) in guestTabs"
                    :key="index"
                  >
                    <b-row>
                      <b-col>Invoice #: {{ guestTab.id }}</b-col>
                      <b-col class="card-icon">
                        Sub Total:
                        {{
                          `${getVendor.local_currency} ${guestTab.stats.sub_total}`
                        }}
                        <b-tooltip
                          target="view-invoice"
                          placement="top"
                          title="Navigate to Guest Tab Section"
                        ></b-tooltip>
                        <i
                          id="view-invoice"
                          class="fi fi-page-pdf large"
                          @click="goToPayments(guestTab.id)"
                        ></i>
                      </b-col>
                    </b-row>
                    <hr />

                    <div
                      v-if="rooms_tabs.length == 0"
                      style="text-align: center"
                    >
                      Restuarant Only Invoice
                    </div>
                    <div v-else>
                      <b-card-text
                        v-for="(room_tab, index) in rooms_tabs"
                        :key="index"
                      >
                        <div v-if="room_tab.invoice_id == guestTab.id">
                          <b-row>
                            <b-col> Unit: {{ room_tab.name }} </b-col>
                            <b-col
                              cols="8"
                              style="text-align: right; font-size: 14px"
                            >
                              {{ room_tab.description }}
                            </b-col>
                          </b-row>
                        </div>
                      </b-card-text>
                    </div>
                  </b-card>
                </div>
              </b-card-body>
            </b-card>

            <!-- Notes Section -->
            <b-card no-body class="section-card" id="notes" ref="notes">
              <b-card-header
                header-tag="header"
                class="p-2 border-bottom-0 text-left"
                role="tab"
              >
                <i class="fi-clipboard mr-2"></i>
                {{ $t("contact_modal.notes_title") }}
              </b-card-header>
              <b-card-body>
                <div v-if="isRequesting">
                  <b-spinner label="Loading..."></b-spinner>
                </div>
                <div v-else>
                  <b-form-group label-for="guestNotes">
                    <b-form-textarea
                      v-model="newGuestNote"
                      id="guestNotes"
                      :placeholder="$t('contact_modal.new_note')"
                      rows="2"
                      type="text"
                    />
                  </b-form-group>

                  <b-button
                    class="note-button"
                    variant="outline-secondary"
                    @click="addGuestNote"
                    v-b-tooltip.hover.top="'Add note to Profile'"
                  >
                    {{ $t("contact_modal.add_note") }}
                  </b-button>

                  <b-card
                    bg-variant="default"
                    v-for="(guestNote, index) in guestNotes"
                    :key="index"
                    class="note-card mb-2"
                  >
                    <b-card-text class="pre-line">{{
                      guestNote.content
                    }}</b-card-text>
                    <template v-slot:header>
                      <small class="text-muted">
                        Note Created: {{ prettyDate(guestNote.created_at) }}
                        <i
                          class="delete-icon float-right fi fi-x"
                          v-b-tooltip.hover.top="'Remove'"
                          @click="removeGuestNote(guestNote.id)"
                        />
                      </small>
                    </template>
                  </b-card>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </b-modal>
    <image-upload-modal ref="upload-modal" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import cloneDeep from "lodash.clonedeep";
import AlertMixin from "@/mixins/alert-mixin";
import { formatNoteDateTime } from "@/utils/date-helper";
export default {
  props: {
    addGuest: {
      type: Function,
      require: false,
      default: null,
    },
    cancel: {
      type: Function,
      require: false,
      default: null,
    },
  },
  components: {
    ImageUploadModal: () => import("@/components/modals/ImageUploadModal"),
  },
  mixins: [AlertMixin],
  data() {
    return {
      guestTabs: [],
      rooms_tabs: [],
      resturant_only_tabs: [],
      guestNotes: [],
      selectedFile: "",
      currentId: -1,
      newGuestNote: "",
      activeSection: "profile",
      sections: [
        {
          id: "profile",
          title: this.$t("contact_modal.profile_title"),
          icon: "fi-torso",
        },
        {
          id: "contact",
          title: this.$t("contact_modal.contact_title"),
          icon: "fi-telephone",
        },
        {
          id: "bookings",
          title: this.$t("contact_modal.trips_title"),
          icon: "fi-calendar",
        },
        {
          id: "notes",
          title: this.$t("contact_modal.notes_title"),
          icon: "fi-clipboard",
        },
      ],
      record: {
        first_name: "",
        last_name: "",
        middle_name: "",
        passport: undefined,
        birthday: "",
        country_of_origin: "",
        citizenship: "",
        sex: "",
        email: "",
        phone: "",
        address: "",
        facebook: "",
        instagram: "",
        twitter: "",
        snapchat: "",
        pinterest: "",
        image_url: "",
        photo_uri: "",
        photo_md_uri: "",
        photo_sm_uri: "",
      },
    };
  },
  computed: {
    ...mapGetters(["isConnected", "getVendor"]),
    ...mapState({
      isRequesting: (state) => state.guestNotes.isRequesting,
      isRequesting: (state) => state.guestPastBookings.isRequesting,
    }),
    getInitials() {
      const first = this.record.first_name
        ? this.record.first_name.charAt(0).toUpperCase()
        : "";
      const last = this.record.last_name
        ? this.record.last_name.charAt(0).toUpperCase()
        : "";
      return `${first}${last}`;
    },
  },
  methods: {
    ...mapActions([
      "updateContact",
      "addContact",
      "createGuestNote",
      "deleteGuestNote",
      "getGuestNotes",
      "getGuestPastBookings",
      "uploadGuestImage",
    ]),
    async show(_record) {
      if (_record) {
        this.record = _record;
      }
      this.currentId = this.record.id;
      this.$refs.contactModal.show();

      // Only fetch notes and bookings for existing contacts (those with an ID)
      if (this.currentId) {
        const guestNotes = await this.getGuestNotes(this.currentId);
        this.guestNotes = guestNotes.reverse();
        this.guestTabs = await this.getGuestPastBookings(this.currentId);
        this.sort_guest_tabs();
      }

      // Set initial active section
      this.activeSection = "profile";
    },
    hide() {
      if (this.cancel) {
        this.$refs.contactModal.hide();
        return this.cancel();
      }
      this.clearData();
      this.$refs.contactModal.hide();
    },
    scrollToSection(sectionId) {
      this.activeSection = sectionId;
      const element = this.$refs[sectionId];
      if (element) {
        this.$refs.contactContent.scrollTop = element.offsetTop - 80;
      }
    },
    handleScroll() {
      // Determine which section is most visible in the viewport
      const scrollTop = this.$refs.contactContent.scrollTop;
      const sections = ["profile", "contact", "bookings", "notes"];
      const offset = 80; // Match the scroll offset used in scrollToSection

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = this.$refs[sections[i]];
        if (section) {
          const sectionTop = section.offsetTop - offset;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollTop >= sectionTop && scrollTop < sectionBottom) {
            this.activeSection = sections[i];
            break;
          }
        }
      }
    },
    selectFileUpload() {
      // Photo upload is not supported at the moment
      this.$refs.fileInput.click();
    },
    onFileChanged(event) {
      this.selectedFile = event.target.files[0];
      if (this.selectedFile) {
        this.onUpload();
      }
    },
    onUpload() {
      const uploadSize = this.selectedFile.size / 2048;
      if (uploadSize >= 1000) {
        this.errorNotification("Maximum image size of 2MB");
        return;
      }
      const formData = new FormData();
      formData.append("image", this.selectedFile, this.selectedFile.name);
      this.$Progress.start();
      if (this.$refs["upload-modal"]) {
        this.$refs["upload-modal"].show();
      }
      this.uploadGuestImage({ formData, guestId: this.currentId })
        .then((res) => {
          const newUri = res;
          this.record.photo_uri = newUri.photo_uri;
          this.record.photo_md_uri = newUri.photo_md_uri;
          this.record.photo_sm_uri = newUri.photo_sm_uri;
          this.successNotification(
            `${this.record.first_name} ${this.record.last_name}'s photo has been updated.`
          );
        })
        .catch((err) => {
          this.errorNotification(
            `${this.record.first_name} ${this.record.last_name}'s photo update failed.`
          );
        })
        .finally(() => {
          this.$Progress.finish();
          if (this.$refs["upload-modal"]) {
            this.$refs["upload-modal"].hide();
          }
        });
    },
    createGuest() {
      if (!this.record.first_name || !this.record.last_name) {
        this.showMessage(
          "All contacts need a valid first and last name",
          "warning"
        );
        return;
      }
      if (
        this.record.first_name.length < 2 ||
        this.record.last_name.length < 2
      ) {
        this.showMessage(
          "All contacts need a valid first and last name",
          "warning"
        );
        return;
      }
      if (this.addGuest) {
        return this.addGuest();
      }
      this.createContact();
    },
    createContact() {
      const newContact = cloneDeep(this.record);
      this.clearData();
      this.$refs.contactModal.hide();
      return this.addContact(newContact);
    },
    editContact() {
      if (
        this.record.first_name.length < 2 ||
        this.record.last_name.length < 2
      ) {
        this.showMessage(
          "All contacts need a valid first and last name",
          "warning"
        );
        return;
      }
      this.updateContact(this.record);
      this.hide();
    },

    clearData() {
      this.record = {};
      this.guestNotes = [];
      this.newGuestNote = "";
      this.guestTabs = [];
      this.rooms_tabs = [];
      this.activeSection = "profile";
    },
    async addGuestNote() {
      if (!this.newGuestNote) {
        this.errorNotification("Please input the note!");
        return;
      }
      const guestNote = {
        guest_id: this.currentId,
        content: this.newGuestNote,
      };
      const newNote = await this.createGuestNote(guestNote);
      this.guestNotes.unshift(newNote);
      this.newGuestNote = "";
    },
    async removeGuestNote(guestNoteId) {
      await this.deleteGuestNote(guestNoteId);
      const deletedRecord = this.guestNotes.find(
        (guestNote) => guestNote.id === guestNoteId
      );
      const currentIndex = this.guestNotes.indexOf(deletedRecord);
      this.guestNotes.splice(currentIndex, 1);
    },
    prettyDate(date) {
      return formatNoteDateTime(date);
    },

    sort_guest_tabs() {
      for (let index = 0; index < this.guestTabs.length; index++) {
        const guestTab = this.guestTabs[index].tab_item;
        for (let index = 0; index < guestTab.length; index++) {
          const reservation_charges = guestTab[index];
          let room = {
            invoice_id: "",
            name: "",
            description: "",
          };

          if (reservation_charges.category_name == "ACCOMMODATIONS") {
            room.invoice_id = reservation_charges.tab_id;
            room.name = reservation_charges.name;
            room.description = reservation_charges.description;
            this.rooms_tabs.push(room);
          }
        }
      }
    },
    goToPayments(data) {
      this.$router.push(`tabs?tab_id=${data}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-modal-wrapper {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

:deep(.modal-dialog) {
  @media (min-width: 775px) and (max-width: 991.98px) {
    width: 90vw !important;
    max-width: 800px !important;
    margin: 1.75rem auto !important;
  }
}

.contact-container {
  max-height: 80vh;
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  flex-direction: column;
  background-color: #f5f7fa;

  @media (min-width: 768px) {
    flex-direction: row;
  }
}

.contact-header {
  position: relative;
  background: white;
  z-index: 10;
  padding-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  order: 1;
}

.contact-sidenav {
  width: 100%;
  background-color: #eef1f6;
  border-right: none;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
  flex-direction: column;
  height: 80vh;
  border-radius: 8px 0 0 8px;
  order: 1;
  width: 200px;
  border-right: 1px solid #dee2e6;
}

.contact-nav-items {
  display: flex;
  flex-direction: column;
  overflow-x: visible;
  padding-bottom: 0;
}

.contact-nav-item {
  padding: 0.6rem 0.75rem;
  margin: 0 0 0.5rem 0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
  }

  &.active {
    background-color: rgba(0, 123, 255, 0.15);
    border-left: 3px solid #007bff;
    font-weight: 500;
    color: #0056b3;
  }
}

.nav-section-title {
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-content {
  flex: 1;
  overflow-y: auto;
  height: 75vh;
  position: relative;
  order: 3;
  background-color: #fff;

  @media (min-width: 768px) {
    height: 80vh;
    border-radius: 0 8px 8px 0;
  }

  @media (max-width: 767.98px) {
    height: 80vh;
    border-radius: 0 0 8px 8px;
  }
}

.contact-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  margin-bottom: 0.25rem;
  opacity: 0.9;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.2);

  &:hover {
    opacity: 0.9;
  }
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  color: #1976d2;
  font-size: 2.2rem;
  font-weight: 500;
}

.contact-body {
  padding-bottom: 2rem;
}

.section-card {
  scroll-margin-top: 70px;
  margin-top: 10px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:deep(.card-header) {
    background-color: #f8f9fa;
    border-bottom-color: #e9ecef;
  }

  &:deep(.card-body) {
    background-color: #ffffff;
    padding: 1.25rem;
  }

  &:deep(.form-control) {
    background-color: #fcfcfc;
    border: 1px solid #ced4da;

    &:focus {
      background-color: #ffffff;
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }
}

.booking-card {
  transition: all 0.2s;
  border-left: 3px solid transparent;
  background-color: #fcfcfc;
  border: 1px solid #e9ecef;

  &:hover {
    border-left-color: #007bff;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
    background-color: #ffffff;
  }
}

.delete-icon {
  cursor: pointer;
  &:hover {
    font-weight: bold;
    color: #dc3545;
  }
}

.note-button {
  position: absolute;
  right: 30px;
  top: 85px;
  border-color: #6c757d;

  &:hover {
    background-color: #6c757d;
    color: #fff;
  }
}

textarea#guestNotes {
  padding-right: 70px;
  background-color: #fcfcfc;

  &:focus {
    background-color: #ffffff;
  }
}

.selected-result-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-icon {
  text-align: right;
  font-size: 20px;

  i {
    cursor: pointer;
    color: #6c757d;
    transition: all 0.2s;

    &:hover {
      color: #007bff;
    }
  }
}

.note-card {
  transition: all 0.2s;
  background-color: #fcfcfc;
  border: 1px solid #e9ecef;

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    background-color: #ffffff;
  }
}

.pre-line {
  white-space: pre-line;
}

.modal-btn {
  cursor: pointer;
  display: block;
  font-size: 14px;
  padding: 7px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.action-btn {
    margin: 0 5px;
    padding: 8px 12px;
    font-size: 16px;

    i.nav-icon {
      font-size: 22px;
    }
  }

  i.nav-icon {
    color: #007bff;

    &:hover {
      color: #0056b3;
    }

    &.cancel-contact {
      color: #dc3545;

      &:hover {
        color: #bd2130;
      }
    }
  }
}

.sidenav-header {
  padding: 1.5rem 0.5rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.sidenav-icon {
  font-size: 2.5rem;
  color: #007bff;
  display: block;
  margin: 0 auto;
}

.contact-name {
  font-weight: 500;
  color: #333;
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desktop-header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}
</style>
